/**
 * The block editor: a flat, ordered list of blocks, with the unsubscribe footer
 * fixed at the end of it.
 *
 * Four decisions from SLICES shape everything below, and each one is visible in
 * the code rather than only in a comment:
 *
 * **The document is flat, so the editor is a list.** No nesting, no columns, no
 * tree. Insert, move and delete are three array operations on one array, which
 * is why they are twelve lines rather than a traversal.
 *
 * **Reordering is buttons and the keyboard, never drag-and-drop.** A drag
 * library is a large dependency and an accessibility problem that has to be
 * solved twice; move-up / move-down is operable by keyboard and screen reader on
 * the first try. Every move control says what it moves and where — "Move heading
 * “Winter sale” up" — because with four headings on screen two anonymous arrows
 * are operable and useless. Alt+↑ / Alt+↓ do the same thing from anywhere in a
 * block, and focus follows the block that moved, so a keyboard user is not
 * dropped back at the top of the page after every step.
 *
 * **Block text is text, and formatting is data about it — never markup.** A
 * heading, a button label, an alt text and the footer lines are `<input>` and
 * `<textarea>`: `<` and `&` mean themselves, and the renderer escapes them into
 * text nodes. A paragraph is the one exception, since slice 56: the owner asked
 * for bold, italic, underline and a size inside a paragraph, and
 * `ParagraphEditor` edits it on a `contentEditable` surface. What it stores is
 * still not markup. It is `runs` — `{ text, bold?, italic?, underline?, size? }`
 * — beside a `text` that is always the runs joined, and the renderer escapes each
 * run's text and wraps it in tags it chooses itself. So there is still no
 * sanitiser here and still nothing to sanitise: the surface is read back through
 * a whitelist walker (`lib/text-runs-dom.ts`) and a paste arrives as its
 * plain-text flavour. "A raw HTML block" was asked for once and stopped by the
 * owner; this is not a road to one.
 *
 * **The footer cannot be deleted or moved off the end.** It is not a block in
 * the list this component reorders: the body and the footer are separate props,
 * so "delete the footer" and "move something below the footer" are not
 * operations that exist here rather than operations that are refused. The card
 * says why in one line, because a control that is missing without explanation
 * reads as a bug.
 *
 * ## Slice 17 — what the fields stopped saying
 *
 * Field descriptions here had grown into paragraphs that argued a case rather
 * than telling somebody what to type. Three arguments moved into this header and
 * one sentence each stayed on the screen:
 *
 *   - **a link cannot contain a variable** because a personalised URL needs
 *     escaping rules and a decision about a contact with no value, and Wend has
 *     neither. The field says "No `{{ variables }}`"; the reasoning is behind
 *     `MoreDetail`, where somebody who wants to know can reach it without
 *     leaving the screen;
 *   - **an image block references an upload, never a URL.** A URL field is a
 *     phishing surface — the target can be swapped after approval, on a host
 *     nobody reviewed — and an unbounded tracking leak that no setting can turn
 *     off. `ImageFields`' own header carries the full argument, including why
 *     slice 11's tracking pixel did not reopen it;
 *   - **the postal address follows the recipient, not the sender.** The example
 *     address and the naming of the three legal regimes fold; the one clause a
 *     person would draw a false conclusion without — that it depends on where
 *     the *recipients* are — is on the field.
 *
 * **What did not move.** The redirect-domain line under a button link stays
 * visible whenever click tracking is on, because the value in that box is then
 * not the address a recipient hovers and the person pasting it is standing right
 * there. `FOOTER_LOCKED_REASON` stays whole on the footer card: it is the only
 * rule in this editor whose consequence lands on the tenant's own AWS account.
 * And the alt-text description keeps the reason alt text matters, softened from
 * "most mail clients block images by default" to "many" — the strong version is
 * email-marketing folklore of exactly the kind this repository has shipped
 * before, and the argument for alt text does not need it.
 *
 * `FOOTER_STAYS_LAST` was printed twice, on the footer card and again on the add
 * bar. It is printed once now, on the card that is missing the controls it
 * explains; the add bar's eyebrow already reads "Add above the footer".
 *
 * ## Hosted images — what moved on the image card, and why
 *
 * How a picture reaches the reader is **the deployment's choice**, and this
 * editor is told it rather than assuming it: `imageDelivery`, from the template
 * view, is `embedded` (the picture travels inside the message as a `cid:` part —
 * the default, and what the live deployment sends) or `hosted` (it is fetched
 * from Wend's own image host when the message is opened). Four things on this
 * screen follow. The second sentence of the first one depends on which mode,
 * and the last exists only where images are hosted:
 *
 *   - **`alt` is the first field on the image card**, above the thumbnail. Field
 *     order is an argument about what matters: for a reader with pictures off,
 *     the alt text is the whole block rather than a caption under a missing one
 *     — and where images are hosted that also means every reader whose client
 *     blocks *remote* content, which a `cid:` image did not. It was third. The
 *     folded paragraph behind it says whichever of those is true here
 *     (`TEMPLATE_IMAGE_ALT_DETAIL[imageDelivery]`), because telling someone on
 *     an embedding deployment that their pictures are "fetched from Wend" is
 *     describing a deployment they are not on.
 *   - **Where images are hosted, a new image block is added with an empty
 *     `alt`**, which makes it unsaveable until somebody writes one. It is seeded
 *     with the file name everywhere else, as on `master`. "banner-final-2.jpg"
 *     satisfies the contract's "not empty" check and ships as the entire content
 *     of the block for everybody who does not see the picture — a much larger
 *     set where pictures are hosted. It was empty in both modes for a while, and
 *     that refused the first save after an upload on a deployment with no image
 *     host, which is a change nobody asked that deployment to take.
 *   - **Size is refused in the browser as well as by the API**, before a
 *     megabyte is uploaded over a phone connection, at
 *     `MAX_TEMPLATE_ASSET_BYTES` — now 1 MB. Nothing here resizes anything:
 *     there is no image library in Wend and none is coming, so the only honest
 *     offer is a clear refusal and a width to aim at.
 *   - **Where images are hosted, a card whose picture has no `publicId` says
 *     so** (`TEMPLATE_IMAGE_UNPUBLISHED_BLOCK_MESSAGE`). The editor page and the
 *     campaign refusal count unpublished pictures and end on "remove that
 *     picture and upload it again", and a count cannot say *which* picture. After
 *     a save has published everything it could, the one left over is the one
 *     saving cannot fix, and this mark is how a person finds it without
 *     re-uploading every picture on the template. Where images are embedded every
 *     `publicId` is null by design and nothing is marked.
 *
 * **Every sentence any of that prints comes from `@wend/shared`.** Not because
 * sharing strings is tidy, but because the same fact is stated by the browser,
 * by the upload endpoint and by multer, and the copy that drifts is always the
 * one quoting a limit that has moved. This slice lowered the cap from 2 MB to
 * 1 MB and found a message still quoting 20 MB from a different feature.
 */
import {
  MAX_BUTTON_TEXT_LENGTH,
  MAX_FOOTER_NOTE_LENGTH,
  MAX_HEADING_TEXT_LENGTH,
  MAX_IMAGE_ALT_LENGTH,
  MAX_IMAGE_BLOCK_WIDTH_PX,
  MAX_SENDER_ADDRESS_LENGTH,
  MAX_TEMPLATE_ASSET_BYTES,
  MAX_TEMPLATE_BLOCKS,
  MAX_UNSUBSCRIBE_LABEL_LENGTH,
  TEMPLATE_IMAGE_ALT_DESCRIPTION,
  TEMPLATE_IMAGE_ALT_DETAIL,
  TEMPLATE_IMAGE_MIME_TYPES,
  TEMPLATE_IMAGE_UNPUBLISHED_BLOCK_MESSAGE,
  BLOCK_ALIGN_VALUES,
  HEADING_LEVEL_VALUES,
  blockAlignSchema,
  headingLevelSchema,
  templateImageNearLimitMessage,
  templateImageTooLargeMessage,
  type BlockAlign,
  type BodyBlock,
  type ButtonBlock,
  type FooterBlock,
  type HeadingBlock,
  type ImageBlock,
  type TemplateImageDelivery,
  type SpacerBlock,
  type TemplateAsset,
  type TextBlock,
} from '@wend/shared';
import {
  Button,
  Callout,
  Card,
  CardContent,
  Checkbox,
  FormField,
  Input,
  Kicker,
  Select,
  cn,
  formatFileSize,
} from '@wend/ui';
import {
  ChevronDown,
  ChevronUp,
  Heading,
  Image as ImageIcon,
  Minus,
  MousePointerClick,
  MoveVertical,
  Plus,
  Text as TextIcon,
  Trash2,
} from 'lucide-react';
import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';

import { FormAlert } from '../../components/FormAlert';
import { Textarea } from '../../components/Textarea';
import { apiBlob } from '../../lib/api/client';
import { errorMessage, isApiError } from '../../lib/api/errors';
import { templateAssetPath, uploadTemplateAsset } from '../../lib/api/templates';
import { redirectDomainInUse, type TrackingKnowledge } from '../../lib/recipient-experience';
import {
  ADDABLE_BLOCK_TYPES,
  BLOCK_ALIGN_LABEL,
  EXTRAS_VARIABLE_HELP,
  FOOTER_LOCKED_REASON,
  FOOTER_STAYS_LAST,
  HEADING_LEVEL_LABEL,
  TEMPLATE_BLOCK_DISPLAY,
  VARIABLE_INSERTS,
  blockIssues,
  deleteBlockLabel,
  moveBlockLabel,
  type AddableBlockType,
  type TemplateIssues,
} from '../../lib/template-display';
import { MoreDetail } from './MoreDetail';
import { ParagraphEditor, type ParagraphEditorHandle } from './ParagraphEditor';

/* -------------------------------------------------------------------------- */
/* Ids and small helpers                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Block ids are safe in a DOM id by contract — `blockIdSchema` restricts them to
 * `A–Z a–z 0–9 _ -` for exactly this reason — so focus can be moved by id
 * without escaping anything.
 */
const cardId = (blockId: string): string => `block-${blockId}`;
const moveButtonId = (blockId: string, direction: 'up' | 'down'): string =>
  `block-${blockId}-move-${direction}`;

/** A new block's id. Stable for the life of the block, and never positional. */
function makeBlockId(): string {
  return crypto.randomUUID();
}

const ALIGN_OPTIONS = BLOCK_ALIGN_VALUES.map((value) => ({
  value,
  label: BLOCK_ALIGN_LABEL[value],
}));

const LEVEL_OPTIONS = HEADING_LEVEL_VALUES.map((value) => ({
  value: String(value),
  label: HEADING_LEVEL_LABEL[value],
}));

/**
 * The link a new button carries until someone replaces it.
 *
 * A button with no link is not a document the contract accepts — `href` is
 * required and must be absolute — so a new block has to start with *something*,
 * and starting invalid would stop the preview compiling until the field was
 * filled in. This placeholder validates, and the block says loudly that it is
 * still a placeholder until it is changed, so it cannot quietly ship.
 */
const PLACEHOLDER_HREF = 'https://example.com';

/* -------------------------------------------------------------------------- */
/* New blocks                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * A newly added block, with every field already at a legal value.
 *
 * The defaults are the schema's own (`level: 2`, `align: 'left'`, a 24px spacer)
 * written out rather than left to zod, because the editor holds the document
 * before it is ever parsed and a half-populated block would show blank controls.
 */
function makeBlock(type: Exclude<AddableBlockType, 'image'>): BodyBlock {
  const id = makeBlockId();
  switch (type) {
    case 'heading':
      return { type: 'heading', id, text: 'A heading', level: 2, align: 'left' };
    case 'text':
      return { type: 'text', id, text: 'Write your message here.', align: 'left' };
    case 'button':
      return { type: 'button', id, text: 'Shop now', href: PLACEHOLDER_HREF, align: 'center' };
    case 'divider':
      return { type: 'divider', id };
    case 'spacer':
      return { type: 'spacer', id, height: 24 };
  }
}

/* -------------------------------------------------------------------------- */
/* Props                                                                       */
/* -------------------------------------------------------------------------- */

export interface BlockEditorProps {
  templateId: string;
  /** Everything above the footer, in order. */
  body: readonly BodyBlock[];
  footer: FooterBlock;
  /** Inline images already uploaded to this template, as the API reports them. */
  assets: readonly TemplateAsset[];
  /**
   * How this deployment carries pictures — `Template.imageDelivery`, the API's
   * one derived answer. It reaches exactly one place, the alt-text reasoning on
   * the image card, which has a sentence that is true in one mode and false in
   * the other. Null only for a template that is not email, which this editor
   * never holds; then no mode-specific sentence is said at all rather than a
   * guessed one.
   */
  imageDelivery: TemplateImageDelivery | null;
  /** What the API said is wrong, routed to the block that caused it. */
  issues: TemplateIssues;
  /**
   * What this workspace's tracking is set to, read once by the editor.
   *
   * It edits nothing and validates nothing, and it reaches exactly one field —
   * the link on a button. That is the one place in this editor where the value
   * an author types is not the value a recipient sees, once Amazon is rewriting
   * every `href`, and the field is where somebody is standing when they paste a
   * URL and decide whether it looks right.
   */
  tracking: TrackingKnowledge;
  /** True while a save is in flight — the document must not move under it. */
  disabled: boolean;
  onChangeBody: (body: BodyBlock[]) => void;
  onChangeFooter: (footer: FooterBlock) => void;
  /** An image that has just been uploaded, so its name and size can be shown. */
  onAssetUploaded: (asset: TemplateAsset) => void;
}

export function BlockEditor({
  templateId,
  body,
  footer,
  assets,
  imageDelivery,
  issues,
  tracking,
  disabled,
  onChangeBody,
  onChangeFooter,
  onAssetUploaded,
}: BlockEditorProps) {
  /**
   * The move that was just made, so focus can follow the block.
   *
   * Without this a keyboard user presses "move up", the list re-renders, focus
   * falls back to the document body and the next press does nothing to the block
   * they were moving. Restoring focus to the same control on the same block is
   * what makes several moves in a row possible at all.
   */
  const [focusAfterMove, setFocusAfterMove] = useState<{
    blockId: string;
    direction: 'up' | 'down';
  } | null>(null);

  useEffect(() => {
    if (focusAfterMove === null) return;
    setFocusAfterMove(null);

    const button = document.getElementById(moveButtonId(focusAfterMove.blockId, focusAfterMove.direction));
    if (button instanceof HTMLButtonElement && !button.disabled) {
      button.focus();
      return;
    }
    // The block reached an end and that button is now disabled, so focus the
    // block itself rather than losing it to the top of the page.
    document.getElementById(cardId(focusAfterMove.blockId))?.focus();
  }, [focusAfterMove]);

  const full = body.length + 1 >= MAX_TEMPLATE_BLOCKS;

  function move(index: number, direction: 'up' | 'down'): void {
    const target = direction === 'up' ? index - 1 : index + 1;
    const block = body[index];
    const swap = body[target];
    if (block === undefined || swap === undefined) return;

    const next = [...body];
    next[index] = swap;
    next[target] = block;
    onChangeBody(next);
    setFocusAfterMove({ blockId: block.id, direction });
  }

  function replace(index: number, block: BodyBlock): void {
    const next = [...body];
    next[index] = block;
    onChangeBody(next);
  }

  function remove(index: number): void {
    onChangeBody(body.filter((_, position) => position !== index));
  }

  function append(block: BodyBlock): void {
    onChangeBody([...body, block]);
    // A block added at the end is a block nobody can see; scrolling to it is
    // what makes "add" feel like it did something on a long template.
    window.requestAnimationFrame(() => {
      document.getElementById(cardId(block.id))?.scrollIntoView({ block: 'center' });
      document.getElementById(cardId(block.id))?.focus();
    });
  }

  return (
    <div className="flex flex-col gap-4">
      {/*
        The empty state of the document itself. It says what is missing and what
        fills it; the consequence — approval is refused — is the part a person
        would otherwise discover by pressing Approve and being told no.
      */}
      {body.length === 0 && (
        <Callout tone="info" title="Nothing in this template but the footer">
          Add a heading or a paragraph below. A template with no message cannot be approved.
        </Callout>
      )}

      <ol className="flex list-none flex-col gap-4">
        {body.map((block, index) => (
          <li key={block.id}>
            <BlockCard
              block={block}
              index={index}
              total={body.length + 1}
              templateId={templateId}
              assets={assets}
              imageDelivery={imageDelivery}
              messages={blockIssues(issues, block.id)}
              tracking={tracking}
              disabled={disabled}
              canMoveUp={index > 0}
              canMoveDown={index < body.length - 1}
              onMove={(direction) => {
                move(index, direction);
              }}
              onChange={(next) => {
                replace(index, next);
              }}
              onDelete={() => {
                remove(index);
              }}
            />
          </li>
        ))}

        <li>
          <FooterCard
            footer={footer}
            position={body.length + 1}
            messages={blockIssues(issues, footer.id)}
            disabled={disabled}
            onChange={onChangeFooter}
          />
        </li>
      </ol>

      <AddBlockBar
        templateId={templateId}
        imageDelivery={imageDelivery}
        disabled={disabled || full}
        full={full}
        onAdd={append}
        onAssetUploaded={onAssetUploaded}
      />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* One block                                                                   */
/* -------------------------------------------------------------------------- */

const BLOCK_ICON: Readonly<Record<BodyBlock['type'] | 'footer', ReactNode>> = {
  heading: <Heading aria-hidden="true" className="size-4" />,
  text: <TextIcon aria-hidden="true" className="size-4" />,
  image: <ImageIcon aria-hidden="true" className="size-4" />,
  button: <MousePointerClick aria-hidden="true" className="size-4" />,
  divider: <Minus aria-hidden="true" className="size-4" />,
  spacer: <MoveVertical aria-hidden="true" className="size-4" />,
  footer: <Minus aria-hidden="true" className="size-4" />,
};

function BlockCard({
  block,
  index,
  total,
  templateId,
  assets,
  imageDelivery,
  messages,
  tracking,
  disabled,
  canMoveUp,
  canMoveDown,
  onMove,
  onChange,
  onDelete,
}: {
  block: BodyBlock;
  index: number;
  total: number;
  templateId: string;
  assets: readonly TemplateAsset[];
  imageDelivery: TemplateImageDelivery | null;
  messages: readonly string[];
  tracking: TrackingKnowledge;
  disabled: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMove: (direction: 'up' | 'down') => void;
  onChange: (block: BodyBlock) => void;
  onDelete: () => void;
}) {
  const display = TEMPLATE_BLOCK_DISPLAY[block.type];
  const invalid = messages.length > 0;

  /** Alt+↑ / Alt+↓ from anywhere inside the block. Plain arrows stay the caret's. */
  function onKeyDown(event: ReactKeyboardEvent<HTMLDivElement>): void {
    if (!event.altKey || event.ctrlKey || event.metaKey) return;
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;

    const direction = event.key === 'ArrowUp' ? 'up' : 'down';
    if (disabled || (direction === 'up' ? !canMoveUp : !canMoveDown)) return;

    event.preventDefault();
    onMove(direction);
  }

  return (
    <Card
      // Focusable so that focus has somewhere to land when a moved block reaches
      // an end and the button that moved it becomes disabled.
      id={cardId(block.id)}
      tabIndex={-1}
      onKeyDown={onKeyDown}
      className={cn('scroll-mt-6', invalid && 'border-bad/45')}
    >
      <CardContent className="flex flex-col gap-4 px-5 py-4">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <span className="grid size-7 shrink-0 place-items-center rounded-sm border border-line bg-white/[0.04] text-muted">
            {BLOCK_ICON[block.type]}
          </span>
          <Kicker as="span">
            {display.label}
          </Kicker>
          <span className="font-mono text-[0.66rem] tracking-[0.1em] text-muted">
            {index + 1} of {total}
          </span>

          <div className="ml-auto flex items-center gap-1.5">
            <MoveButton
              id={moveButtonId(block.id, 'up')}
              label={moveBlockLabel(block, 'up', { index, total })}
              disabled={disabled || !canMoveUp}
              onClick={() => {
                onMove('up');
              }}
            >
              <ChevronUp aria-hidden="true" className="size-4" />
            </MoveButton>

            <MoveButton
              id={moveButtonId(block.id, 'down')}
              label={moveBlockLabel(block, 'down', { index, total })}
              disabled={disabled || !canMoveDown}
              onClick={() => {
                onMove('down');
              }}
            >
              <ChevronDown aria-hidden="true" className="size-4" />
            </MoveButton>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label={deleteBlockLabel(block)}
              disabled={disabled}
              className="text-muted hover:text-bad"
              onClick={onDelete}
            >
              <Trash2 aria-hidden="true" className="size-4" />
            </Button>
          </div>
        </div>

        {/* The API's own verdict on this block, against the block that caused it. */}
        {invalid && (
          <div role="alert" className="flex flex-col gap-1.5 rounded-base border border-bad/40 bg-bad/[0.08] px-3.5 py-3">
            {messages.map((message) => (
              <p key={message} className="text-[0.86rem] leading-snug text-text">
                {message}
              </p>
            ))}
          </div>
        )}

        <BlockFields
          block={block}
          templateId={templateId}
          assets={assets}
          imageDelivery={imageDelivery}
          tracking={tracking}
          disabled={disabled}
          onChange={onChange}
        />
      </CardContent>
    </Card>
  );
}

/**
 * A move control.
 *
 * Icon-only on screen and fully named to assistive technology: the label carries
 * the block type, a quotation of what it says, and where it currently is. That
 * label *is* the reordering interface for anyone not using a mouse, which is why
 * it is built by `moveBlockLabel` in one place rather than written per call.
 */
function MoveButton({
  id,
  label,
  disabled,
  onClick,
  children,
}: {
  id: string;
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Button
      type="button"
      id={id}
      variant="ghost"
      size="sm"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}

/* -------------------------------------------------------------------------- */
/* The fields of each block type                                               */
/* -------------------------------------------------------------------------- */

function BlockFields({
  block,
  templateId,
  assets,
  imageDelivery,
  tracking,
  disabled,
  onChange,
}: {
  block: BodyBlock;
  templateId: string;
  assets: readonly TemplateAsset[];
  imageDelivery: TemplateImageDelivery | null;
  tracking: TrackingKnowledge;
  disabled: boolean;
  onChange: (block: BodyBlock) => void;
}) {
  switch (block.type) {
    case 'heading':
      return <HeadingFields block={block} disabled={disabled} onChange={onChange} />;
    case 'text':
      return <TextFields block={block} disabled={disabled} onChange={onChange} />;
    case 'button':
      return (
        <ButtonFields block={block} tracking={tracking} disabled={disabled} onChange={onChange} />
      );
    case 'image':
      return (
        <ImageFields
          block={block}
          templateId={templateId}
          assets={assets}
          imageDelivery={imageDelivery}
          disabled={disabled}
          onChange={onChange}
        />
      );
    case 'spacer':
      return <SpacerFields block={block} disabled={disabled} onChange={onChange} />;
    case 'divider':
      return (
        <p className="text-[0.85rem] text-muted">
          A horizontal rule. It has nothing to configure.
        </p>
      );
  }
}

function AlignField({
  value,
  disabled,
  onChange,
  label = 'Alignment',
}: {
  value: BlockAlign;
  disabled: boolean;
  onChange: (align: BlockAlign) => void;
  label?: string;
}) {
  return (
    <FormField label={label}>
      <Select
        value={value}
        options={ALIGN_OPTIONS}
        disabled={disabled}
        size="sm"
        onValueChange={(next) => {
          onChange(blockAlignSchema.catch(value).parse(next));
        }}
      />
    </FormField>
  );
}

function HeadingFields({
  block,
  disabled,
  onChange,
}: {
  block: HeadingBlock;
  disabled: boolean;
  onChange: (block: HeadingBlock) => void;
}) {
  return (
    <div className="flex flex-col gap-4">
      <VariableTextField
        label="Heading"
        required
        multiline={false}
        maxLength={MAX_HEADING_TEXT_LENGTH}
        value={block.text}
        disabled={disabled}
        onChange={(text) => {
          onChange({ ...block, text });
        }}
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <FormField label="Size">
          <Select
            value={String(block.level)}
            options={LEVEL_OPTIONS}
            disabled={disabled}
            size="sm"
            onValueChange={(next) => {
              onChange({ ...block, level: headingLevelSchema.catch(block.level).parse(Number(next)) });
            }}
          />
        </FormField>
        <AlignField
          value={block.align}
          disabled={disabled}
          onChange={(align) => {
            onChange({ ...block, align });
          }}
        />
      </div>
    </div>
  );
}

/**
 * A paragraph — the one block whose text can carry formatting.
 *
 * The field used to say "Plain text. Line breaks are kept and become line
 * breaks in the email." The first half stopped being true in slice 56 and the
 * second is what the surface now shows by keeping them, so the line went rather
 * than being reworded into a sentence about formatting the toolbar already
 * states by existing.
 *
 * The variable tokens insert through the editor's handle rather than by
 * splicing `block.text`: a paragraph with runs has two representations of its
 * text, and an edit to one of them is the edit the contract refuses.
 * `MAX_TEXT_BLOCK_LENGTH` is not enforced as you type — a `contentEditable` has
 * no `maxLength` — and is refused by the contract, against this block, on the
 * next preview.
 */
function TextFields({
  block,
  disabled,
  onChange,
}: {
  block: TextBlock;
  disabled: boolean;
  onChange: (block: TextBlock) => void;
}) {
  const editor = useRef<ParagraphEditorHandle>(null);

  return (
    <div className="flex flex-col gap-4">
      <FormField label="Paragraph" required>
        <div className="flex flex-col gap-2">
          <ParagraphEditor
            ref={editor}
            label="Paragraph"
            block={block}
            disabled={disabled}
            onChange={onChange}
          />
          <VariableInserts
            disabled={disabled}
            onInsert={(token) => {
              editor.current?.insert(token);
            }}
          />
        </div>
      </FormField>

      <div className="sm:max-w-[16rem]">
        <AlignField
          value={block.align}
          disabled={disabled}
          onChange={(align) => {
            onChange({ ...block, align });
          }}
        />
      </div>
    </div>
  );
}

/**
 * A button — and the one field in this editor whose value a recipient does not
 * necessarily receive.
 *
 * With click tracking on, Amazon rewrites every `href` as it sends so that a
 * click passes through a redirect first. The author's URL is still where the
 * reader ends up; it is simply not the address they hover, and it is not the
 * address in this box. That is said under the field rather than only in the
 * panel at the top of the tab, because this is where somebody pastes a link and
 * asks whether it looks trustworthy — which is the entire argument for refusing
 * click tracking until the redirect runs on a domain the workspace owns.
 *
 * Nothing is said when tracking is off, still being read, or switched on with
 * nothing provisioned. In those states the link in this box is the link that
 * arrives, and a caveat about a rewrite that is not happening is noise in a
 * field somebody is trying to type a URL into.
 */
function ButtonFields({
  block,
  tracking,
  disabled,
  onChange,
}: {
  block: ButtonBlock;
  tracking: TrackingKnowledge;
  disabled: boolean;
  onChange: (block: ButtonBlock) => void;
}) {
  const redirectDomain = redirectDomainInUse(tracking);

  return (
    <div className="flex flex-col gap-4">
      <VariableTextField
        label="Button label"
        required
        multiline={false}
        maxLength={MAX_BUTTON_TEXT_LENGTH}
        value={block.text}
        disabled={disabled}
        onChange={(text) => {
          onChange({ ...block, text });
        }}
      />

      <FormField
        label="Link"
        required
        description="Absolute — https://, http:// or mailto:. No {{ variables }}."
      >
        <Input
          value={block.href}
          spellCheck={false}
          disabled={disabled}
          className="font-mono text-[0.9rem]"
          onChange={(event) => {
            onChange({ ...block, href: event.target.value });
          }}
        />
      </FormField>

      <MoreDetail summary="Why a link cannot contain a variable">
        <p>
          A personalised URL has encoding rules — escaping the substituted value, and deciding what
          happens when a contact has no value for it — that Wend does not have yet. A link with a
          token in it is refused at save rather than compiled into something that half works.
        </p>
      </MoreDetail>

      {/*
        Kind 2, and it stays visible: with click tracking on, the value in this
        box is not the address a recipient hovers. Somebody pasting a URL and
        asking whether it looks trustworthy is standing right here, and a person
        who only reads the field would conclude their own domain is what arrives.
        The rest of the story — that Wend cannot show the rewritten link because
        it does not exist until SES makes it — is in `previewGap` on the Preview
        tab and in this file's header, not repeated under every button.
      */}
      {redirectDomain !== null && (
        <p className="max-w-prose text-[0.82rem] leading-relaxed text-muted">
          Click tracking is on, so recipients see a link on{' '}
          <span className="font-mono text-[0.9em] text-muted">{redirectDomain}</span> instead of
          this one. Amazon rewrites it as the message is sent.
        </p>
      )}

      {block.href === PLACEHOLDER_HREF && (
        <Callout tone="warn" title="Still pointing at the placeholder link">
          A new button starts at{' '}
          <span className="font-mono text-[0.82rem] text-text">{PLACEHOLDER_HREF}</span>. Replace
          it, or a campaign will send people there.
        </Callout>
      )}

      <div className="sm:max-w-[16rem]">
        <AlignField
          value={block.align}
          disabled={disabled}
          onChange={(align) => {
            onChange({ ...block, align });
          }}
        />
      </div>
    </div>
  );
}

function SpacerFields({
  block,
  disabled,
  onChange,
}: {
  block: SpacerBlock;
  disabled: boolean;
  onChange: (block: SpacerBlock) => void;
}) {
  return (
    <div className="sm:max-w-[16rem]">
      <FormField label="Height" description="Between 4 and 120 pixels.">
        <Input
          type="number"
          min={4}
          max={120}
          value={String(block.height)}
          disabled={disabled}
          onChange={(event) => {
            const height = Number.parseInt(event.target.value, 10);
            // A field being cleared mid-edit is not a height of zero; leaving the
            // last legal value in place keeps the document valid while typing.
            if (Number.isFinite(height)) onChange({ ...block, height });
          }}
        />
      </FormField>
    </div>
  );
}

/**
 * An inline image.
 *
 * The block references an **asset id**, never a URL — see `imageBlockSchema`, and
 * the reasons are worth remembering here: a URL field in an email is both a
 * phishing surface (the target can be swapped after approval, because we do not
 * own it) and a tracking leak (a remote image is a request carrying the reader's
 * IP to a third party nobody named). So the only way to get an image into a
 * template is to upload one, and the only thing this control edits is which
 * uploaded image it is, plus how it is described and sized.
 *
 * **Slice 11 put a fair question to that second reason, and it survives.** Open
 * tracking is a remote image: a 1×1 pixel Amazon injects, which reports the
 * reader's IP and the moment they opened the message. So Wend now does, for
 * workspaces that ask for it, the thing this field was closed to prevent. The
 * difference is not the mechanism, it is who agreed to it — the pixel is one
 * request, to the workspace's own SES account, behind a switch that is off by
 * default and that states in plain words what it does. A URL typed into a
 * template is an unbounded number of requests, to a host nobody reviewed, that
 * no setting can switch off and that no disclosure covers. Being able to promise
 * "off means off" is exactly what makes the pixel a choice rather than a
 * default, and that promise dies the moment a template can name any host it
 * likes. The field stays closed.
 *
 * **HOSTED IMAGES CHANGE HALF OF THAT, WHERE THEY ARE SWITCHED ON, AND THE HALF
 * THEY CHANGE IS NAMED ABOVE.** In a deployment with an image host every image
 * is fetched from a URL rather than carried in the message, so "a remote image
 * is a request carrying the reader's IP" is no longer something Wend avoids
 * there — it is something Wend does, on every image, to every recipient. (With
 * no host, pictures are embedded as they always were and none of this
 * paragraph applies.) What survives intact is the *phishing* half: the URL is one
 * Wend mints for an object Wend stores, so there is nothing for anyone to swap
 * after approval. The tracking half is answered only by who receives the request
 * and by the promise that the URL carries no recipient-specific component —
 * operational commitments, written down in `imageBlockSchema` in `@wend/shared`,
 * which this component cannot enforce and must not claim to. None of it argues
 * for an author-typed `src`, which would give back both halves at once.
 */
function ImageFields({
  block,
  templateId,
  assets,
  imageDelivery,
  disabled,
  onChange,
}: {
  block: ImageBlock;
  templateId: string;
  assets: readonly TemplateAsset[];
  imageDelivery: TemplateImageDelivery | null;
  disabled: boolean;
  onChange: (block: ImageBlock) => void;
}) {
  const asset = assets.find((candidate) => candidate.id === block.assetId) ?? null;

  /*
   * `byteSize === null` is "the API did not report a size", which is not a size
   * of zero and must not produce a sentence about one. `templateImageNearLimitMessage`
   * answers only about a number it was given, so the null is handled here rather
   * than coerced into a `?? 0` that would quietly claim every unmeasured image
   * is tiny.
   */
  const nearLimit =
    asset === null || asset.byteSize === null ? null : templateImageNearLimitMessage(asset.byteSize);

  return (
    <div className="flex flex-col gap-4">
      {/*
        ALT TEXT IS FIRST NOW, ABOVE THE PICTURE ITSELF.
        Field order is an argument about what matters: for a reader who does not
        see the picture this field is the entire block — and where this
        deployment hosts pictures, that includes every reader whose client blocks
        remote content. It used to sit third, under the thumbnail and the file
        name, which is where a person puts the thing they fill in last.
      */}
      <VariableTextField
        label="Alt text"
        required
        multiline={false}
        maxLength={MAX_IMAGE_ALT_LENGTH}
        description={TEMPLATE_IMAGE_ALT_DESCRIPTION}
        value={block.alt}
        disabled={disabled}
        onChange={(alt) => {
          onChange({ ...block, alt });
        }}
      />
      {/*
        The reasoning folds; the instruction above does not. Slice 17's rule —
        short by default, detail on demand, and progressive is not deleted.

        The paragraph is the one that is TRUE OF THIS DEPLOYMENT, keyed on the
        delivery mode the API reported. With no mode (a non-email template, which
        this card never shows) nothing is said rather than a guess.
      */}
      {imageDelivery !== null && (
        <MoreDetail summary="Why this matters more than it looks">
          <p>{TEMPLATE_IMAGE_ALT_DETAIL[imageDelivery]}</p>
        </MoreDetail>
      )}

      <div className="flex flex-wrap items-start gap-4">
        <AssetPreview templateId={templateId} assetId={block.assetId} alt={block.alt} />
        <div className="min-w-0 flex-1">
          {asset === null ? (
            <p className="text-[0.85rem] text-warn">
              This block references an image that is not in this template&rsquo;s uploads. Saving
              will be refused until it is.
            </p>
          ) : (
            <>
              <p className="truncate text-[0.9rem] text-text" title={asset.filename ?? undefined}>
                {asset.filename ?? 'Uploaded image'}
              </p>
              <p className="font-mono text-[0.7rem] uppercase tracking-[0.1em] text-muted">
                {asset.mimeType ?? 'unknown type'}
                {/* No size rather than a zero: see `nearLimit` above. */}
                {asset.byteSize === null ? '' : ` · ${formatFileSize(asset.byteSize)}`}
              </p>
              {nearLimit !== null && <p className="mt-1.5 text-[0.85rem] text-warn">{nearLimit}</p>}
              {/*
                WHICH picture has no address, where this deployment hosts pictures.
                The page-level callout and the campaign refusal count ("1 of the
                3") and end on "remove that picture and upload it again"; a count
                cannot say which one. The mode test is load-bearing: embedded
                pictures all have a null `publicId`, so without it every card on
                the live deployment would be marked. And `=== null` is spelled out
                rather than `!asset.publicId`, the `!null` shape this repository
                has shipped inverted before.
              */}
              {imageDelivery === 'hosted' && asset.publicId === null && (
                <p className="mt-1.5 text-[0.85rem] text-warn">{TEMPLATE_IMAGE_UNPUBLISHED_BLOCK_MESSAGE}</p>
              )}
            </>
          )}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <FormField
          label="Width"
          description={`In pixels, up to ${String(MAX_IMAGE_BLOCK_WIDTH_PX)}. Leave it empty to fill the width of the email.`}
        >
          <Input
            type="number"
            min={16}
            max={MAX_IMAGE_BLOCK_WIDTH_PX}
            value={block.width === null ? '' : String(block.width)}
            placeholder="Full width"
            disabled={disabled}
            onChange={(event) => {
              const raw = event.target.value.trim();
              const width = raw === '' ? null : Number.parseInt(raw, 10);
              onChange({ ...block, width: width === null || Number.isFinite(width) ? width : block.width });
            }}
          />
        </FormField>
        <AlignField
          value={block.align}
          disabled={disabled}
          onChange={(align) => {
            onChange({ ...block, align });
          }}
        />
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* The footer                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * The unsubscribe footer.
 *
 * It renders like any other block — the same card, the same fields — and it is
 * the absence of two controls that makes it different: there is no delete and
 * there are no move buttons, because the footer is not part of the list the
 * editor reorders. The line under the header says why, in one sentence, rather
 * than leaving a person to discover it by pressing something that does nothing.
 *
 * There is deliberately **no unsubscribe URL field**. The link is minted per
 * recipient at send time; a field here would let a template point its unsubscribe
 * at a page that unsubscribes nobody, which is worse than no link at all because
 * it looks compliant.
 */
function FooterCard({
  footer,
  position,
  messages,
  disabled,
  onChange,
}: {
  footer: FooterBlock;
  position: number;
  messages: readonly string[];
  disabled: boolean;
  onChange: (footer: FooterBlock) => void;
}) {
  const invalid = messages.length > 0;

  return (
    <Card id={cardId(footer.id)} tabIndex={-1} className={cn(invalid && 'border-bad/45')}>
      <CardContent className="flex flex-col gap-4 px-5 py-4">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <span className="grid size-7 shrink-0 place-items-center rounded-sm border border-line bg-white/[0.04] text-muted">
            {BLOCK_ICON.footer}
          </span>
          <Kicker as="span">
            {TEMPLATE_BLOCK_DISPLAY.footer.label}
          </Kicker>
          <span className="font-mono text-[0.66rem] tracking-[0.1em] text-muted">
            {position} of {position} · always last
          </span>
        </div>

        {/* Why there is no delete here, and why the block above cannot move
            down. Said next to the missing controls rather than left for someone
            to discover by pressing one that does nothing. */}
        <p className="max-w-prose text-[0.85rem] leading-relaxed text-muted">
          {FOOTER_LOCKED_REASON} {FOOTER_STAYS_LAST}
        </p>

        {invalid && (
          <div role="alert" className="flex flex-col gap-1.5 rounded-base border border-bad/40 bg-bad/[0.08] px-3.5 py-3">
            {messages.map((message) => (
              <p key={message} className="text-[0.86rem] leading-snug text-text">
                {message}
              </p>
            ))}
          </div>
        )}

        {/*
          The consequence is stated at the point of the decision, not in a
          tooltip and not after the fact. Unchecking this is legitimate — a
          service notice has nothing to unsubscribe from — but on a commercial
          send it is how a tenant's own AWS account gets reviewed, and the
          mechanism is worth one sentence: a recipient who wants out and cannot
          find how presses the spam button, and a complaint costs strictly more
          than an unsubscribe.

          Four words visible, the rest folded, per the text rule. The warning
          only appears once it is off, because a checked box needs no argument.
        */}
        <label className="flex items-start gap-3">
          <Checkbox
            checked={footer.includeUnsubscribe}
            disabled={disabled}
            onCheckedChange={(checked) => {
              onChange({ ...footer, includeUnsubscribe: checked === true });
            }}
          />
          <span className="flex flex-col gap-1">
            <span className="text-[0.92rem] font-medium leading-snug text-text">
              Include an unsubscribe link
            </span>
            <span className="text-[0.84rem] leading-snug text-muted">
              Required for marketing email. Leave it off only for service notices.
            </span>
          </span>
        </label>

        {!footer.includeUnsubscribe && (
          <div
            role="note"
            className="rounded-base border border-warn/40 bg-warn/[0.08] px-3.5 py-3 text-[0.86rem] leading-snug text-text"
          >
            Without a link, a recipient who wants out will use their mail client's spam button
            instead. Complaints count against your own Amazon account, and Amazon reviews accounts
            above 0.1%.
          </div>
        )}

        {footer.includeUnsubscribe && (
          <FormField
            label="Unsubscribe link text"
            required
            description="The wording only — Wend mints the link itself, per recipient, at send time."
          >
            <Input
              value={footer.unsubscribeLabel}
              maxLength={MAX_UNSUBSCRIBE_LABEL_LENGTH}
              disabled={disabled}
              onChange={(event) => {
                onChange({ ...footer, unsubscribeLabel: event.target.value });
              }}
            />
          </FormField>
        )}

        {/*
          The short form keeps the one fact that decides whether to fill it in —
          it depends on where the *recipient* is — because somebody who reads
          only "Optional" and mails a list with US addresses on it has drawn a
          false conclusion. Everything else about it is instruction and folds.
        */}
        <FormField
          label="Postal address"
          description="Optional in India. Required where your recipients are in the US, Canada or the EU."
        >
          <div className="flex flex-col gap-2">
            <Textarea
              rows={3}
              value={footer.senderAddress ?? ''}
              maxLength={MAX_SENDER_ADDRESS_LENGTH}
              disabled={disabled}
              onChange={(event) => {
                const value = event.target.value;
                onChange({ ...footer, senderAddress: value.trim() === '' ? null : value });
              }}
            />
            <MoreDetail summary="What to put here">
              <p>
                Your business address, shown in small print at the bottom of the email so a reader
                can see who sent it — for example “Acme Retail Pvt Ltd, 1 MG Road, Bengaluru
                560001”.
              </p>
              <p>
                Anti-spam law in the US, Canada and the EU requires it, and which law applies
                follows the recipient rather than the sender. India requires nothing here.
              </p>
            </MoreDetail>
          </div>
        </FormField>

        <FormField
          label="Why am I getting this? (optional)"
          description="e.g. “You are receiving this because you bought from us in the last year.”"
        >
          <Textarea
            rows={2}
            value={footer.note ?? ''}
            maxLength={MAX_FOOTER_NOTE_LENGTH}
            disabled={disabled}
            onChange={(event) => {
              const value = event.target.value;
              onChange({ ...footer, note: value.trim() === '' ? null : value });
            }}
          />
        </FormField>
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* Adding                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * The add bar, and it sits **below the footer card** on purpose.
 *
 * A new block is appended to the end of the body — that is, immediately above
 * the footer — and putting the control where the block will appear is the only
 * way to say so without a sentence. The footer does not move; nothing is ever
 * added after it.
 */
function AddBlockBar({
  templateId,
  imageDelivery,
  disabled,
  full,
  onAdd,
  onAssetUploaded,
}: {
  templateId: string;
  /** Decides only what a new image block's `alt` starts as — see the add path. */
  imageDelivery: TemplateImageDelivery | null;
  disabled: boolean;
  full: boolean;
  onAdd: (block: BodyBlock) => void;
  onAssetUploaded: (asset: TemplateAsset) => void;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<{
    message: string;
    requestId: string | undefined;
  } | null>(null);
  /** What to say about a picture that went through but is close to the cap. */
  const [uploadWarning, setUploadWarning] = useState<string | null>(null);

  async function onFileChosen(file: File): Promise<void> {
    setUploadError(null);
    setUploadWarning(null);

    /*
     * THE SIZE IS CHECKED HERE, BEFORE THE REQUEST, AND THE SERVER CHECKS IT
     * AGAIN.
     *
     * This one is a courtesy and the server's is the rule. The courtesy is worth
     * having because the alternative is watching a 4 MB photograph upload over a
     * phone connection in order to be told it was never going to be accepted —
     * and because a refusal that arrives instantly reads as a limit, while one
     * that arrives after a progress bar reads as a failure.
     *
     * The sentence is the contract's, not this component's, so the browser and
     * the API cannot end up quoting different numbers at the same person. Wend
     * does not resize pictures — there is no image library anywhere in it — so
     * there is nothing to offer here but a clear no and a width to aim at.
     */
    if (file.size > MAX_TEMPLATE_ASSET_BYTES) {
      setUploadError({ message: templateImageTooLargeMessage(file.name, file.size), requestId: undefined });
      return;
    }

    setUploading(true);
    try {
      const asset = await uploadTemplateAsset(templateId, file);
      onAssetUploaded(asset);
      // Said once, at the moment the file was chosen, and again on the block
      // itself for as long as the image is on the template. `null` when the file
      // is nowhere near the cap, which is the ordinary case.
      setUploadWarning(templateImageNearLimitMessage(file.size));
      onAdd({
        type: 'image',
        id: makeBlockId(),
        assetId: asset.id,
        /*
         * WHERE PICTURES ARE HOSTED: EMPTY, AND THE BLOCK IS UNSAVEABLE UNTIL
         * SOMEBODY WRITES IT. WHERE THEY ARE EMBEDDED: THE FILE NAME, AS BEFORE.
         *
         * A seeded file name is a poor alt: it passes the contract's "not empty"
         * check and ships as the entire content of the block for the reader who
         * does not see the picture. Where this deployment hosts pictures that
         * reader is every client blocking remote content — a large minority — so
         * the block starts empty, the editor shows
         * `TEMPLATE_IMAGE_ALT_REQUIRED_MESSAGE` against it, and neither the
         * preview nor the save accepts the document until it is written. That
         * friction is the product owner's decision for hosting ("prominent and
         * never empty"), and it costs a sentence, once, per image.
         *
         * It was first applied in both modes, and that was a change to the
         * unconfigured deployment beyond anything asked for: on `master`, adding a
         * picture and pressing Save worked; on this branch with no image host, the
         * same first save was refused. The rule for this slice is that a
         * deployment with no image host behaves as `master` does apart from the
         * two approved changes (the 1 MB cap and the attachment warning), so where
         * pictures are embedded — and where the mode is unknown, which this email
         * editor never is — the block is seeded exactly as `master` seeds it. The
         * alt field is still first on the card and still says who reads it.
         */
        alt: imageDelivery === 'hosted' ? '' : (asset.filename ?? 'Image'),
        width: null,
        align: 'center',
      });
    } catch (error) {
      setUploadError({
        message: errorMessage(error),
        requestId: isApiError(error) ? error.requestId : undefined,
      });
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="flex flex-col gap-3 rounded-base border border-dashed border-line-strong bg-white/[0.02] px-4 py-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="mr-1 font-mono text-[0.66rem] uppercase tracking-[0.14em] text-muted">
          Add above the footer
        </span>

        {ADDABLE_BLOCK_TYPES.map((type) =>
          type === 'image' ? (
            <Button
              key={type}
              type="button"
              variant="secondary"
              size="sm"
              loading={uploading}
              disabled={disabled}
              leadingIcon={<Plus aria-hidden="true" />}
              onClick={() => {
                fileInput.current?.click();
              }}
            >
              {TEMPLATE_BLOCK_DISPLAY[type].label}
            </Button>
          ) : (
            <Button
              key={type}
              type="button"
              variant="secondary"
              size="sm"
              disabled={disabled}
              leadingIcon={<Plus aria-hidden="true" />}
              onClick={() => {
                onAdd(makeBlock(type));
              }}
            >
              {TEMPLATE_BLOCK_DISPLAY[type].label}
            </Button>
          ),
        )}

        <input
          ref={fileInput}
          type="file"
          accept={TEMPLATE_IMAGE_MIME_TYPES.join(',')}
          className="hidden"
          onChange={(event) => {
            const file = event.target.files?.[0];
            // Clearing the input is what lets the same file be chosen twice.
            event.target.value = '';
            if (file !== undefined) void onFileChosen(file);
          }}
        />
      </div>

      {/*
        `FOOTER_STAYS_LAST` used to be printed here as well as on the footer card
        two rows above. One page, one sentence: the eyebrow on this bar already
        says "Add above the footer", and the card that is missing a move-down
        button is where the rule needs explaining. A second copy is the one that
        goes stale, because it is the one nobody remembers is there.
      */}
      {full && (
        <p className="text-[0.85rem] text-warn">
          {MAX_TEMPLATE_BLOCKS} blocks is the most one template holds. Delete one to add another.
        </p>
      )}

      {/*
        A form's verdict on a file that was just offered, so `FormAlert` rather
        than `ErrorState` — nothing failed to *load* here. The request id travels
        with it because an upload refused by the server is the failure a person
        cannot diagnose from the screen.
      */}
      {uploadError !== null && (
        <FormAlert
          tone="error"
          {...(uploadError.requestId === undefined ? {} : { requestId: uploadError.requestId })}
        >
          {uploadError.message}
        </FormAlert>
      )}

      {/*
        A warning, not a verdict: the file is uploaded and the block is on the
        template. `info` rather than `error` because `error` is this component's
        vocabulary for "your submission was refused", and nothing here was — the
        caution is in the sentence, which says the image will send. No request id
        either, for the same reason: there is no failure to look up.
      */}
      {uploadWarning !== null && <FormAlert tone="info">{uploadWarning}</FormAlert>}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* A text field that can insert a variable                                     */
/* -------------------------------------------------------------------------- */

/**
 * A plain-text field with the variable tokens beside it.
 *
 * The tokens are inserted **at the caret**, not appended, because `{{ name }}`
 * belongs in the middle of "Hello , your order is ready" far more often than at
 * the end of it — and a control that puts it in the wrong place is one a person
 * stops using after the second time.
 *
 * The list of tokens comes from the contact contract via `VARIABLE_INSERTS`.
 * Anything typed by hand that a contact cannot supply is refused at save, by
 * name, which is what the messages above the fields are.
 */
function VariableTextField({
  label,
  description,
  required,
  multiline,
  maxLength,
  value,
  disabled,
  onChange,
}: {
  label: string;
  description?: string;
  required?: boolean;
  multiline: boolean;
  maxLength: number;
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  // One ref for either control, set by a callback so the two element types do
  // not need two refs and a cast between them.
  const control = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  function insert(token: string): void {
    const element = control.current;
    if (element === null) {
      onChange(`${value}${token}`);
      return;
    }

    const start = element.selectionStart ?? value.length;
    const end = element.selectionEnd ?? start;
    onChange(`${value.slice(0, start)}${token}${value.slice(end)}`);

    // The value is controlled, so the caret has to be restored after React has
    // written the new one.
    window.requestAnimationFrame(() => {
      element.focus();
      const caret = start + token.length;
      element.setSelectionRange(caret, caret);
    });
  }

  return (
    <FormField
      label={label}
      {...(required === true ? { required: true } : {})}
      {...(description === undefined ? {} : { description })}
    >
      <div className="flex flex-col gap-2">
        {multiline ? (
          <Textarea
            ref={(node) => {
              control.current = node;
            }}
            rows={4}
            value={value}
            maxLength={maxLength}
            disabled={disabled}
            onChange={(event) => {
              onChange(event.target.value);
            }}
          />
        ) : (
          <Input
            ref={(node) => {
              control.current = node;
            }}
            value={value}
            maxLength={maxLength}
            disabled={disabled}
            onChange={(event) => {
              onChange(event.target.value);
            }}
          />
        )}

        <VariableInserts disabled={disabled} onInsert={insert} />
      </div>
    </FormField>
  );
}

/**
 * The row of variable tokens under a text field.
 *
 * One component for both kinds of field since slice 56: a plain field splices
 * the token into its value at the caret, a paragraph hands it to its editor,
 * which inserts it into the runs. The buttons are the same either way, and a
 * second copy of them would be the one whose tooltip goes stale.
 */
function VariableInserts({
  disabled,
  onInsert,
}: {
  disabled: boolean;
  onInsert: (token: string) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="font-mono text-[0.62rem] uppercase tracking-[0.14em] text-muted">
        Insert
      </span>
      {VARIABLE_INSERTS.map((variable) => (
        <button
          key={variable.token}
          type="button"
          disabled={disabled}
          title={variable.meaning}
          // A paragraph keeps its selection only while focus stays in it.
          onMouseDown={(event) => {
            event.preventDefault();
          }}
          onClick={() => {
            onInsert(variable.token);
          }}
          className={cn(
            'rounded-sm border border-line px-2 py-0.5 font-mono text-[0.7rem] text-muted',
            'transition-colors duration-150 hover:border-brand-a/50 hover:text-text',
            'disabled:cursor-not-allowed disabled:opacity-55',
          )}
        >
          {variable.token}
        </button>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Showing an uploaded image                                                   */
/* -------------------------------------------------------------------------- */

/**
 * The thumbnail of an inline image.
 *
 * The bytes are behind the tenant guard, so a bare `<img src="/api/…">` would go
 * out without an `Authorization` header and come back a 401. They are fetched
 * with the session and handed to the `<img>` as an object URL, which this
 * component owns and revokes — an unrevoked one keeps the blob alive for the
 * life of the page.
 */
function AssetPreview({
  templateId,
  assetId,
  alt,
}: {
  templateId: string;
  assetId: string;
  alt: string;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    let objectUrl: string | null = null;

    setUrl(null);
    setFailed(false);

    void (async () => {
      try {
        const blob = await apiBlob(templateAssetPath(templateId, assetId), controller.signal);
        if (controller.signal.aborted) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      } catch {
        if (!controller.signal.aborted) setFailed(true);
      }
    })();

    return () => {
      controller.abort();
      if (objectUrl !== null) URL.revokeObjectURL(objectUrl);
    };
  }, [templateId, assetId]);

  return (
    <div className="grid size-24 shrink-0 place-items-center overflow-hidden rounded-base border border-line bg-white/[0.04]">
      {url === null ? (
        <span className="px-2 text-center font-mono text-[0.6rem] uppercase tracking-[0.1em] text-muted">
          {failed ? 'not available' : 'loading'}
        </span>
      ) : (
        <img src={url} alt={alt} className="size-full object-contain" />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Help                                                                        */
/* -------------------------------------------------------------------------- */

/**
 * The one line about variables the editor shows above the block list.
 *
 * The sentence that stays is what a variable *does* — it is replaced per
 * recipient at send time — because a person who thinks the braces are literal
 * text will write around them. Which uploaded columns work, and how to spell
 * one, is instruction and folds.
 */
export function VariableHelp() {
  return (
    <div className="flex flex-col gap-2 rounded-base border border-line bg-white/[0.02] px-4 py-3.5">
      <Kicker as="span">
        Variables
      </Kicker>
      <p className="max-w-prose text-[0.85rem] leading-relaxed text-muted">
        Replaced per recipient when the campaign sends.{' '}
        {VARIABLE_INSERTS.map((variable) => variable.token).join(', ')} come from the contact.
      </p>
      <MoreDetail summary="Using a column from an uploaded list">
        <p>{EXTRAS_VARIABLE_HELP}</p>
      </MoreDetail>
    </div>
  );
}
